import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Select,
  MenuItem,
  Menu,
  IconButton,
  Tooltip,
  TextField,
  Chip,
  Slider,
} from '@mui/material';
import {
  Play,
  MessageSquare,
  QrCode,
  ExternalLink,
  Minimize2,
  Tv,
  Radio,
  Volume2,
  VolumeX,
} from 'lucide-react';
import GridOnIcon from '@mui/icons-material/GridOn';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DevicesIcon from '@mui/icons-material/Devices';
import QrCodeIcon from '@mui/icons-material/QrCode';
import ConnectedDevicesModal from '../ConnectedDevicesModal';

export default function ScreenControlsCard({
  currentIntroVideo,
  currentTransitionVideo,
  defaultIntro = process.env.VITE_DEFAULT_INTRO_VIDEO_CODE || process.env.REACT_APP_DEFAULT_INTRO_VIDEO_CODE || 'H_bB0sAqLNg',
  defaultTransition = process.env.VITE_DEFAULT_TRANSITION_VIDEO_CODE || process.env.REACT_APP_DEFAULT_TRANSITION_VIDEO_CODE || 'wpXZZ4f_RKY',
  getIntros,
  getTransitions,
  handleChangeIntro,
  handleChangeTransition,
  openVideoPlayer,
  showHideModalMessage,
  showModalMessage,
  showHideModalQR,
  showModalQR,
  showHideModalVotesTable,
  showModalVotesTable,
  hasVotes = false,
  onResetVotes,
  allowInteractivity,
  onToggleIntegratedScreen,
  openConfigModal,
  userSettings,
  isRemoteControl = false,
  screenVolume,
  onUpdateScreenVolume,
  userId,
  onTriggerManualVotation,
  currentPlaylistItem,
  playlist = [],
  isIntegratedScreen = false,
  videoPlayer,
  videoId,
}) {
  const { t } = useTranslation();
  const [screenMessageText, setScreenMessageText] = useState('');
  const [qrMenuAnchorEl, setQrMenuAnchorEl] = useState(null);
  const [votesMenuAnchorEl, setVotesMenuAnchorEl] = useState(null);
  const [openConnectedDevicesModal, setOpenConnectedDevicesModal] = useState(false);
  const effectiveHostId = userId || userSettings?.uid;

  // Resolución exhaustiva del cantante asignado al track activo
  const assignedPerformerName = (() => {
    // 1. Directo desde item o estructura anidada de currentPlaylistItem
    const direct = currentPlaylistItem?.item?.assignedTo || currentPlaylistItem?.assignedTo;
    if (typeof direct === 'string' && direct.trim()) return direct.trim();

    // 2. Desde la playlist activa buscando por videoId
    const activeVid = currentPlaylistItem?.item?.videoId || currentPlaylistItem?.videoId || videoId;
    if (activeVid && Array.isArray(playlist)) {
      const found = playlist.find(it => (it.videoId === activeVid || it.id === activeVid || it.item?.videoId === activeVid));
      const plSinger = found?.assignedTo || found?.item?.assignedTo;
      if (typeof plSinger === 'string' && plSinger.trim()) return plSinger.trim();
    }

    // 3. Fallback a datos persistidos de sesión / Firestore
    try {
      const cvd = userSettings?.currentVideoData;
      if (cvd) {
        const parsed = typeof cvd === 'string' ? JSON.parse(cvd) : cvd;
        if (parsed?.assignedTo && typeof parsed.assignedTo === 'string' && parsed.assignedTo.trim()) {
          return parsed.assignedTo.trim();
        }
      }
      const cps = userSettings?.currentPlaybackState;
      if (cps) {
        const parsed = typeof cps === 'string' ? JSON.parse(cps) : cps;
        if (parsed?.assignedTo && typeof parsed.assignedTo === 'string' && parsed.assignedTo.trim()) {
          return parsed.assignedTo.trim();
        }
      }
    } catch (e) { }

    return '';
  })();

  const hasAssignedPerformer = Boolean(assignedPerformerName && assignedPerformerName.length > 0);

  const isScreenActive = isIntegratedScreen
    ? Boolean(videoId)
    : isRemoteControl
      ? true
      : (Boolean(videoPlayer && !videoPlayer.closed) || Boolean(videoId));

  const canTriggerManualVotation = Boolean(
    allowInteractivity &&
    hasAssignedPerformer &&
    isScreenActive &&
    typeof onTriggerManualVotation === 'function'
  );

  const getManualVotationTooltip = () => {
    if (!allowInteractivity) return 'Interactividad desactivada en configuración';
    if (!hasAssignedPerformer) return 'El track actual no tiene un cantante/intérprete asignado';
    if (!isScreenActive) return 'La pantalla principal no está activa';
    return `Lanzar votación en vivo a todos los monitores (${assignedPerformerName})`;
  };

  const effectiveScreenVolume = screenVolume !== undefined
    ? Math.max(0, Math.min(100, Math.round(screenVolume)))
    : Math.max(0, Math.min(100, Math.round(userSettings?.configs?.screenSettings?.volume ?? 100)));

  const handleScreenVolumeChange = (val, commit) => {
    if (typeof onUpdateScreenVolume === 'function') {
      onUpdateScreenVolume(val, commit);
    }
  };

  const truncate50 = (str) => {
    if (!str) return '';
    return str.length > 50 ? str.substring(0, 50) : str;
  };

  const introsList = typeof getIntros === 'function' ? getIntros() : (
    (userSettings?.configs?.intros || [])
      .map((intro, index) => {
        if (!intro) return null;
        const introId = typeof intro === 'object' ? intro.id : intro;
        const introName = (typeof intro === 'object' && intro.name) ? intro.name : `Mi Intro ${index + 1}`;
        if (!introId) return null;
        const displayName = truncate50(introName);
        return (
          <MenuItem 
            key={`sc_intro_${index}_${introId}`} 
            value={introId}
            title={displayName}
            sx={{
              fontSize: '0.78rem',
              maxWidth: 320,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block'
            }}
          >
            {displayName}
          </MenuItem>
        );
      })
      .filter(Boolean)
  );

  const transitionsList = typeof getTransitions === 'function' ? getTransitions() : (
    (userSettings?.configs?.transitions || [])
      .map((transition, index) => {
        if (!transition) return null;
        const transId = typeof transition === 'object' ? transition.id : transition;
        const transName = (typeof transition === 'object' && transition.name) ? transition.name : `Mi Transición ${index + 1}`;
        if (!transId) return null;
        const displayName = truncate50(transName);
        return (
          <MenuItem 
            key={`sc_trans_${index}_${transId}`} 
            value={transId}
            title={displayName}
            sx={{
              fontSize: '0.78rem',
              maxWidth: 320,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block'
            }}
          >
            {displayName}
          </MenuItem>
        );
      })
      .filter(Boolean)
  );

  const getSelectedIntroName = (val) => {
    if (!val || val === defaultIntro) return t('header.defaultIntro');
    const item = (userSettings?.configs?.intros || []).find((i) => {
      const id = typeof i === 'object' ? i.id : i;
      return id === val;
    });
    if (item && typeof item === 'object' && item.name) {
      return truncate50(item.name);
    }
    return truncate50(val);
  };

  const getSelectedTransitionName = (val) => {
    if (!val || val === defaultTransition) return t('header.defaultTransition');
    const item = (userSettings?.configs?.transitions || []).find((tItem) => {
      const id = typeof tItem === 'object' ? tItem.id : tItem;
      return id === val;
    });
    if (item && typeof item === 'object' && item.name) {
      return truncate50(item.name);
    }
    return truncate50(val);
  };

  const handleSendMessage = (e) => {
    const input = document.getElementById('modalMessage');
    const inputVal = input ? input.value : screenMessageText;
    if (showHideModalMessage) {
      showHideModalMessage(inputVal, e);
    }
  };

  return (
    <Card 
      className="pannel-screen-controls-card"
      sx={{
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        background: 'rgba(18, 8, 32, 0.88) !important',
        backdropFilter: 'blur(20px) !important',
        border: '1px solid rgba(0, 255, 136, 0.25) !important',
        borderRadius: '16px !important',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6), 0 0 20px rgba(0, 255, 136, 0.08) !important',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      <CardContent sx={{ p: '14px 16px !important', width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
        {/* Cabecera de la Card */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '8px',
                background: 'rgba(0, 255, 136, 0.15)',
                border: '1px solid rgba(0, 255, 136, 0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#00ff88',
                boxShadow: '0 0 10px rgba(0, 255, 136, 0.3)'
              }}
            >
              <Tv size={16} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.86rem', color: '#ffffff', letterSpacing: '0.3px' }}>
              {t('screenControls.title')}
            </Typography>
            <Chip 
              size="small" 
              label={t('common.options')} 
              icon={<Radio size={12} color="#00ff88" style={{ animation: 'pulse 1.5s infinite' }} />}
              sx={{ 
                height: 20, 
                fontSize: '0.66rem', 
                fontWeight: 700,
                background: 'rgba(0, 255, 136, 0.16)', 
                color: '#00ff88',
                border: '1px solid rgba(0, 255, 136, 0.35)',
                '& .MuiChip-icon': { ml: '4px' }
              }} 
            />
          </Box>

          {!isRemoteControl && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
              <Tooltip title={t('header.openFloating')}>
                <IconButton
                  size="small"
                  onClick={() => {
                    if (openVideoPlayer) openVideoPlayer(undefined, true);
                  }}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    p: '5px',
                    '&:hover': { color: '#00d4ff', background: 'rgba(0, 212, 255, 0.15)', borderColor: '#00d4ff' }
                  }}
                >
                  <ExternalLink size={14} />
                </IconButton>
              </Tooltip>

              <Tooltip title={t('header.detachFloating')}>
                <IconButton
                  size="small"
                  onClick={() => {
                    if (onToggleIntegratedScreen) onToggleIntegratedScreen(false);
                  }}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    p: '5px',
                    '&:hover': { color: '#ff2d9b', background: 'rgba(255, 45, 155, 0.15)', borderColor: '#ff2d9b' }
                  }}
                >
                  <Minimize2 size={14} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* Fila 1: Selección de Intro y Transición - Exactamente 50% y 50% sin desbordar */}
        <Box 
          sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', 
            gap: 1, 
            mb: 1.2,
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          {/* Mitad 1: Intro */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5, 
              minWidth: 0, 
              width: '100%', 
              maxWidth: '100%',
              overflow: 'hidden' 
            }}
          >
            <Select 
              value={currentIntroVideo || defaultIntro}
              onChange={(e) => {
                if (e.target.value === '__add_intro__') {
                  if (openConfigModal) openConfigModal(2);
                  return;
                }
                if (handleChangeIntro) handleChangeIntro(e);
              }}
              size="small"
              fullWidth
              renderValue={(selectedVal) => {
                const label = getSelectedIntroName(selectedVal);
                return (
                  <span
                    style={{
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: '100%'
                    }}
                    title={label}
                  >
                    {label}
                  </span>
                );
              }}
              sx={{
                flex: '1 1 0',
                minWidth: 0,
                width: '100%',
                fontSize: '0.76rem',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.04)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '&:hover': { background: 'rgba(255, 255, 255, 0.07)' },
                '& .MuiSelect-select': {
                  py: '5px',
                  pl: '8px',
                  pr: '22px !important',
                  fontSize: '0.74rem',
                  lineHeight: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: 0,
                  overflow: 'hidden',
                  width: '100%',
                  boxSizing: 'border-box'
                },
                '& .MuiSelect-icon': {
                  right: '2px',
                  color: 'rgba(255, 255, 255, 0.5)'
                }
              }}
            >
              <MenuItem value={defaultIntro}>{t('header.defaultIntro')}</MenuItem> 
              {introsList}
              {!isRemoteControl && (
                <MenuItem 
                  value="__add_intro__" 
                  sx={{ 
                    color: '#ff2d9b', 
                    fontWeight: 600, 
                    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                    fontSize: '0.76rem',
                    '&:hover': { background: 'rgba(255, 45, 155, 0.12)' }
                  }}
                >
                  {t('screenControls.configIntros')}
                </MenuItem>
              )}
            </Select>

            <Tooltip title={t('screenControls.playIntroNow')}>
              <IconButton 
                size="small"
                onClick={() => openVideoPlayer(currentIntroVideo || defaultIntro)} 
                sx={{
                  color: '#c7d2fe',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  p: 0,
                  width: 28,
                  height: 28,
                  minWidth: 28,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': { color: '#ff2d9b', borderColor: 'rgba(255, 45, 155, 0.4)', background: 'rgba(255, 45, 155, 0.12)' }
                }}
              >
                <Play size={13} fill="currentColor" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Mitad 2: Transición */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5, 
              minWidth: 0, 
              width: '100%', 
              maxWidth: '100%',
              overflow: 'hidden' 
            }}
          >
            <Select 
              value={currentTransitionVideo || defaultTransition}
              onChange={(e) => {
                if (e.target.value === '__add_transition__') {
                  if (openConfigModal) openConfigModal(3);
                  return;
                }
                if (handleChangeTransition) handleChangeTransition(e);
              }}
              size="small"
              fullWidth
              renderValue={(selectedVal) => {
                const label = getSelectedTransitionName(selectedVal);
                return (
                  <span
                    style={{
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: '100%'
                    }}
                    title={label}
                  >
                    {label}
                  </span>
                );
              }}
              sx={{
                flex: '1 1 0',
                minWidth: 0,
                width: '100%',
                fontSize: '0.76rem',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.04)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '&:hover': { background: 'rgba(255, 255, 255, 0.07)' },
                '& .MuiSelect-select': {
                  py: '5px',
                  pl: '8px',
                  pr: '22px !important',
                  fontSize: '0.74rem',
                  lineHeight: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: 0,
                  overflow: 'hidden',
                  width: '100%',
                  boxSizing: 'border-box'
                },
                '& .MuiSelect-icon': {
                  right: '2px',
                  color: 'rgba(255, 255, 255, 0.5)'
                }
              }}
            >
              <MenuItem value={defaultTransition}>{t('header.defaultTransition')}</MenuItem> 
              {transitionsList}
              {!isRemoteControl && (
                <MenuItem 
                  value="__add_transition__" 
                  sx={{ 
                    color: '#00d4ff', 
                    fontWeight: 600, 
                    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                    fontSize: '0.76rem',
                    '&:hover': { background: 'rgba(0, 212, 255, 0.12)' }
                  }}
                >
                  {t('screenControls.configTransitions')}
                </MenuItem>
              )}
            </Select>

            <Tooltip title={t('screenControls.playTransitionNow')}>
              <IconButton 
                size="small"
                onClick={() => openVideoPlayer(currentTransitionVideo)} 
                sx={{
                  color: '#c7d2fe',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  p: 0,
                  width: 28,
                  height: 28,
                  minWidth: 28,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': { color: '#b44dff', borderColor: 'rgba(180, 77, 255, 0.4)', background: 'rgba(180, 77, 255, 0.12)' }
                }}
              >
                <Play size={13} fill="currentColor" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Fila 2: Mensaje en Pantalla + Acciones de Interacción (QR y Votos) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <TextField
            placeholder={t('screenControls.placeholderMessage')}
            id="modalMessage"
            variant="outlined"
            size="small"
            value={screenMessageText}
            onChange={(e) => setScreenMessageText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                fontSize: '0.8rem',
                background: 'rgba(255, 255, 255, 0.04)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                height: 36,
                '& fieldset': { border: 'none' },
                '& input::placeholder': {
                  color: 'rgba(255, 255, 255, 0.4)',
                  opacity: 1
                }
              }
            }}
          />

          {/* Botón Enviar / Ocultar Mensaje */}
          <Tooltip title={showModalMessage ? t('modals.clearScreenMessage') : t('modals.sendScreenMessage')}>
            <IconButton 
              size="small"
              onClick={(e) => handleSendMessage(e)}
              sx={{
                color: showModalMessage ? '#ff2d9b' : 'rgba(255, 255, 255, 0.7)',
                background: showModalMessage ? 'rgba(255, 45, 155, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                border: '1px solid',
                borderColor: showModalMessage ? '#ff2d9b' : 'rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                p: '7px',
                boxShadow: showModalMessage ? '0 0 10px rgba(255, 45, 155, 0.5)' : 'none',
                '&:focus': { outline: 'none' },
                '@media (hover: hover)': {
                  '&:hover': {
                    color: '#ff2d9b',
                    background: 'rgba(255, 45, 155, 0.25)',
                    borderColor: '#ff2d9b'
                  }
                }
              }}
            >
              <MessageSquare size={16} />
            </IconButton>
          </Tooltip>

          {/* Botón QR de Apareamiento y Menú de Monitores */}
          <Tooltip title="Opciones de Monitor y QR">
            <span>
              <IconButton 
                size="small"
                onClick={(e) => setQrMenuAnchorEl(e.currentTarget)} 
                disabled={!allowInteractivity}
                sx={{
                  color: showModalQR || Boolean(qrMenuAnchorEl) ? '#00d4ff' : 'rgba(255, 255, 255, 0.7)',
                  background: showModalQR || Boolean(qrMenuAnchorEl) ? 'rgba(0, 212, 255, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid',
                  borderColor: showModalQR || Boolean(qrMenuAnchorEl) ? '#00d4ff' : 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  p: '7px',
                  boxShadow: showModalQR || Boolean(qrMenuAnchorEl) ? '0 0 10px rgba(0, 212, 255, 0.5)' : 'none',
                  '&:focus': { outline: 'none' },
                  '@media (hover: hover)': {
                    '&:hover': {
                      color: '#00d4ff',
                      background: 'rgba(0, 212, 255, 0.25)',
                      borderColor: '#00d4ff'
                    }
                  },
                  '&.Mui-disabled': { opacity: 0.35 }
                }}
              >
                <QrCode size={16} />
              </IconButton>
            </span>
          </Tooltip>

          <Menu
            anchorEl={qrMenuAnchorEl}
            open={Boolean(qrMenuAnchorEl)}
            onClose={() => setQrMenuAnchorEl(null)}
            PaperProps={{
              sx: {
                backgroundColor: 'rgba(18, 20, 32, 0.95)',
                backdropFilter: 'blur(16px)',
                border: '1px solid rgba(0, 212, 255, 0.3)',
                borderRadius: '12px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.7), 0 0 15px rgba(0,212,255,0.2)',
                color: '#ffffff',
                minWidth: '220px',
                '& .MuiMenuItem-root': {
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  gap: '10px',
                  padding: '10px 16px',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 212, 255, 0.12)',
                    color: '#00d4ff',
                  }
                }
              }
            }}
          >
            <MenuItem
              onClick={() => {
                setQrMenuAnchorEl(null);
                if (showHideModalQR) showHideModalQR();
              }}
            >
              <QrCodeIcon fontSize="small" sx={{ color: '#00d4ff' }} />
              {showModalQR ? 'Ocultar QR en Pantalla' : 'Mostrar QR en Pantalla'}
            </MenuItem>
            <MenuItem
              onClick={() => {
                setQrMenuAnchorEl(null);
                setOpenConnectedDevicesModal(true);
              }}
            >
              <DevicesIcon fontSize="small" sx={{ color: '#00d4ff' }} />
              Ver Monitores Vinculados
            </MenuItem>
          </Menu>

          {/* Botón y Submenú de Tabla de Votos */}
          <Tooltip title={t('screenControls.votesTooltip', 'Opciones de tabla de votaciones')}>
            <span>
              <IconButton 
                size="small"
                onClick={(e) => setVotesMenuAnchorEl(e.currentTarget)} 
                disabled={!allowInteractivity}
                sx={{
                  color: showModalVotesTable ? '#b44dff' : 'rgba(255, 255, 255, 0.7)',
                  background: showModalVotesTable ? 'rgba(180, 77, 255, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid',
                  borderColor: showModalVotesTable ? '#b44dff' : 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  p: '7px',
                  boxShadow: showModalVotesTable ? '0 0 10px rgba(180, 77, 255, 0.5)' : 'none',
                  '&:focus': { outline: 'none' },
                  '@media (hover: hover)': {
                    '&:hover': {
                      color: '#b44dff',
                      background: 'rgba(180, 77, 255, 0.25)',
                      borderColor: '#b44dff'
                    }
                  },
                  '&.Mui-disabled': { opacity: 0.35 }
                }}
              >
                <GridOnIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>

          <Menu
            anchorEl={votesMenuAnchorEl}
            open={Boolean(votesMenuAnchorEl)}
            onClose={() => setVotesMenuAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            PaperProps={{
              sx: {
                backgroundColor: '#120b22 !important',
                background: '#120b22 !important',
                border: '1px solid rgba(180, 77, 255, 0.3) !important',
                borderRadius: '14px !important',
                boxShadow: '0 16px 40px rgba(0, 0, 0, 0.9), 0 0 20px rgba(180, 77, 255, 0.15) !important',
                color: '#ffffff',
                minWidth: '230px',
                p: '6px 8px !important',
                '& .MuiMenuItem-root': {
                  fontSize: '0.8rem !important',
                  fontWeight: '500 !important',
                  borderRadius: '8px',
                  py: '7px !important',
                  px: '10px !important',
                  display: 'flex',
                  alignItems: 'center',
                  lineHeight: 1.4,
                  '&:hover': {
                    backgroundColor: 'rgba(180, 77, 255, 0.15)',
                    color: '#b44dff',
                  },
                  '&.Mui-disabled': {
                    opacity: 0.35,
                  }
                }
              }
            }}
          >
            <MenuItem
              onClick={(e) => {
                e?.currentTarget?.blur();
                setVotesMenuAnchorEl(null);
                if (showHideModalVotesTable) showHideModalVotesTable();
              }}
            >
              <GridOnIcon sx={{ fontSize: 16, color: '#b44dff', mr: 1.5, flexShrink: 0 }} />
              <span style={{ fontSize: '0.8rem' }}>
                {showModalVotesTable ? t('screenControls.hideVotesTable', 'Ocultar Tabla de Resultados') : t('screenControls.showVotesTable', 'Mostrar Tabla de Resultados')}
              </span>
            </MenuItem>

            <MenuItem
              onClick={(e) => {
                e?.currentTarget?.blur();
                setVotesMenuAnchorEl(null);
                if (onResetVotes) onResetVotes();
              }}
              disabled={!hasVotes}
              sx={{
                color: hasVotes ? '#ff4d6d !important' : 'rgba(255, 255, 255, 0.35) !important',
                '&:hover': {
                  backgroundColor: 'rgba(255, 77, 109, 0.15) !important',
                  color: '#ff4d6d !important',
                }
              }}
            >
              <RestartAltIcon sx={{ fontSize: 17, color: hasVotes ? '#ff4d6d' : 'rgba(255, 255, 255, 0.3)', mr: 1.5, flexShrink: 0 }} />
              <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                {t('screenControls.resetVotes', 'Reiniciar Votaciones')}
              </span>
            </MenuItem>
          </Menu>

          {/* Botón Lanzar Votación Manual */}
          <Tooltip title={getManualVotationTooltip()}>
            <span>
              <IconButton 
                size="small"
                onClick={(e) => {
                  e?.currentTarget?.blur();
                  if (onTriggerManualVotation) onTriggerManualVotation();
                }} 
                disabled={!canTriggerManualVotation}
                sx={{
                  color: canTriggerManualVotation ? '#ff2d9b' : 'rgba(255, 255, 255, 0.4)',
                  background: canTriggerManualVotation ? 'rgba(255, 45, 155, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid',
                  borderColor: canTriggerManualVotation ? 'rgba(255, 45, 155, 0.45)' : 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  p: '7px',
                  boxShadow: canTriggerManualVotation ? '0 0 12px rgba(255, 45, 155, 0.35)' : 'none',
                  '&:focus': { outline: 'none' },
                  '@media (hover: hover)': {
                    '&:hover': {
                      color: '#ffffff',
                      background: 'rgba(255, 45, 155, 0.35)',
                      borderColor: '#ff2d9b',
                      boxShadow: '0 0 15px rgba(255, 45, 155, 0.6)'
                    }
                  },
                  '&.Mui-disabled': { opacity: 0.35 }
                }}
              >
                <HowToVoteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Fila 3: Volumen de Pantalla / Música Sincronizado */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.2, 
            mt: 1.2, 
            px: 1.2, 
            py: 0.6,
            borderRadius: '8px', 
            background: 'rgba(255, 255, 255, 0.03)', 
            border: '1px solid rgba(255, 255, 255, 0.08)' 
          }}
        >
          <Box 
            onClick={() => handleScreenVolumeChange(effectiveScreenVolume === 0 ? 100 : 0, true)}
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.6, 
              color: (effectiveScreenVolume === 0) ? 'rgba(255, 255, 255, 0.35)' : '#00ff88',
              cursor: 'pointer',
              userSelect: 'none'
            }}
            title={effectiveScreenVolume === 0 ? t('header.unmute') || "Activar sonido" : t('header.mute') || "Silenciar"}
          >
            {effectiveScreenVolume === 0 ? <VolumeX size={15} /> : <Volume2 size={15} />}
            <Typography sx={{ fontSize: '0.74rem', fontWeight: 600, color: 'rgba(255, 255, 255, 0.85)', whiteSpace: 'nowrap' }}>
              {t('header.screenVolume') || 'Volumen'}
            </Typography>
          </Box>
          <Slider
            size="small"
            value={effectiveScreenVolume}
            min={0}
            max={100}
            step={1}
            onChange={(e, val) => handleScreenVolumeChange(val, false)}
            onChangeCommitted={(e, val) => handleScreenVolumeChange(val, true)}
            onDoubleClick={() => handleScreenVolumeChange(100, true)}
            slotProps={{
              thumb: {
                title: "Doble clic para restablecer al 100%"
              }
            }}
            sx={{
              color: '#00ff88',
              height: 4,
              flex: 1,
              py: 0.8,
              '& .MuiSlider-thumb': {
                width: 12,
                height: 12,
                boxShadow: '0 0 6px #00ff88',
                '&:hover, &.Mui-focusVisible': {
                  boxShadow: '0 0 10px #00ff88',
                }
              },
              '& .MuiSlider-rail': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
              }
            }}
          />
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#00ff88', minWidth: 32, textAlign: 'right' }}>
            {effectiveScreenVolume}%
          </Typography>
        </Box>
      </CardContent>

      {/* Modal de Dispositivos Conectados en Vivo */}
      <ConnectedDevicesModal
        open={openConnectedDevicesModal}
        onClose={() => setOpenConnectedDevicesModal(false)}
        hostId={effectiveHostId}
        onOpenQr={() => showHideModalQR && showHideModalQR()}
      />
    </Card>
  );
}
